import {Omit} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton/IconButton';
import {Theme} from '@material-ui/core/styles';
import {fade} from '@material-ui/core/styles/colorManipulator';
import TableCell, {TableCellProps} from '@material-ui/core/TableCell';
import Tooltip from '@material-ui/core/Tooltip/Tooltip';
import EditIcon from '@material-ui/icons/Edit';
import ErrorIcon from '@material-ui/icons/Error';
import makeStyles from '@material-ui/styles/makeStyles';
import * as classNames from 'classnames';
import * as React from 'react';
import {useRef, useState} from 'react';
import DefaultEditDialog, {DefaultEditDialogProps} from './DefaultEditDialog';
import {TableState} from './redux/actions';
import useTableState from './redux/useTableState';

interface HighLightBehaviorProps {
  highlightContentOnHover?: 'row' | 'edit-button';
}

export type EditableTableCellProps = HighLightBehaviorProps &
  Omit<TableCellProps, 'onChange'> &
  Pick<DefaultEditDialogProps, 'name' | 'value' | 'validate' | 'onChange'> &
  Pick<TableState, 'columns'>;

export const useRowHoverStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      borderRadius: theme.spacing(0.5),
      marginLeft: theme.spacing(-1),
      height: theme.spacing(4),
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      '& > button': {
        padding: theme.spacing(0.5),
        marginRight: theme.spacing(-1),
      },
    },
    alignRight: {
      justifyContent: 'flex-end',
      '& svg:first-child': {
        marginRight: 'auto',
      },
    },
    alignRightIconPadding: {
      paddingRight: theme.spacing(4),
    },
    alignLeft: {
      '& > button': {
        marginLeft: 'auto',
      },
    },
    highlightOnRowHover: {
      cursor: 'text',
      '$tableRow:hover &': {
        backgroundColor: fade(theme.palette.common.white, 0.5),
      },
      '$tableRow:hover &:hover': {
        backgroundColor: theme.palette.common.white,
      },
      width: '100%',
    },
    highlight: {
      backgroundColor: theme.palette.common.white,
      transition: theme.transitions.create('background-color', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    tableRow: {},
  };
});

export default function EditableTableCell({
  children,
  onChange,
  value,
  name,
  validate,
  highlightContentOnHover,
  align,
  ...otherProps
}: EditableTableCellProps) {
  const [hover, setHover] = useState(false);
  const [editing, setEditing] = useState(false);
  const [renderDialog, setRenderDialog] = useState(false);
  const [editButtonHover, setEditButtonHover] = useState(false);
  const anchorEl = useRef(null);
  const columns = useTableState(({columns}) => columns);
  const {
    root,
    highlightOnRowHover: highlightOnRowHoverClass,
    highlight: highlightClass,
    alignLeft,
    alignRight,
    alignRightIconPadding,
  } = useRowHoverStyles();
  const handleOnMouseOver = () => {
    setHover(true);
  };
  const handleOnMouseLeave = () => {
    setHover(false);
  };
  const handleEditButtonOnMouseOver = () => {
    setEditButtonHover(true);
  };
  const handleEditButtonOnMouseLeave = () => {
    setEditButtonHover(false);
  };
  const handleEdit = () => {
    setEditing(true);
    setRenderDialog(true);
  };
  const handleClose = () => {
    setHover(false);
    setEditing(false);
    setEditButtonHover(false);
  };
  const handleExited = () => {
    setRenderDialog(false);
  };
  const error = validate ? validate({value}, null) : undefined;
  if (!columns || !columns[name]) {
    return null;
  }
  const {
    displayName,
    type,
    options,
    orderBy,
    maxLength,
    maxValue,
    minValue,
    decimalScale,
    creatable,
    hideCreatedOption,
  } = columns[name];
  const editIcon =
    hover && !editing ? (
      <IconButton
        className="cla-edit-cell"
        onMouseOver={handleEditButtonOnMouseOver}
        onMouseLeave={handleEditButtonOnMouseLeave}
        onClick={handleEdit}
      >
        <EditIcon />
      </IconButton>
    ) : (
      undefined
    );
  return (
    <TableCell ref={anchorEl} {...otherProps}>
      <span
        className={classNames(root, {
          [highlightOnRowHoverClass]: highlightContentOnHover === 'row',
          [highlightClass]: highlightContentOnHover === 'edit-button' && editButtonHover,
          [alignLeft]: align === 'left',
          [alignRight]: align === 'right',
          [alignRightIconPadding]: align === 'right' && !editIcon,
        })}
        onClick={highlightContentOnHover === 'row' ? handleEdit : undefined}
        onMouseOver={renderDialog ? undefined : handleOnMouseOver}
        onMouseLeave={handleOnMouseLeave}
      >
        {error && error.value ? (
          <Tooltip title={error ? error.value : undefined}>
            <ErrorIcon className="cla-error-icon" color="error" />
          </Tooltip>
        ) : (
          undefined
        )}
        {children}
        {editIcon}
      </span>
      {renderDialog ? (
        <DefaultEditDialog
          name={name}
          displayName={displayName}
          anchorEl={anchorEl.current}
          open={editing}
          onClose={handleClose}
          value={value}
          type={type}
          onChange={onChange}
          onExited={handleExited}
          options={options}
          validate={validate}
          orderBy={orderBy}
          maxLength={maxLength}
          maxValue={maxValue}
          minValue={minValue}
          decimalScale={decimalScale}
          creatable={creatable}
          hideCreatedOption={hideCreatedOption}
        />
      ) : (
        undefined
      )}
    </TableCell>
  );
}
